/**
 * Hooks TanStack Query para DTEs Recibidos.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type { SiiMontos } from '@/modules/purchases/hooks/usePurchases'

/** Evento SII (acuse/aceptación/reclamo) de un DTE recibido (Ley 19.983). */
export interface SiiEvent {
  id: string
  tipo: 'ERM' | 'ACD' | 'RCD' | 'RFP' | 'RFT'
  tipo_display: string
  sii_ok: boolean
  sii_codigo: string
  sii_glosa: string
  registered_at: string
  registered_by?: string | null
  registered_by_name?: string | null
  sii_fecha_evento?: string
  rut_responsable?: string
  /** Fase 2b: artefacto firmado (EnvioRecibos/RespuestaDTE) y envío al emisor. */
  signed?: boolean
  sent_to_emisor_at?: string | null
  sent_to_emisor?: string
}

export interface InboundDTE {
  id: string
  source: 'email' | 'upload'
  sender_email: string
  subject: string
  rut_emisor: string
  razon_social_emisor: string
  tipo_dte: number | null
  folio: number | null
  fecha_emision: string | null
  monto_total: string | null
  /** Montos no facturables (MontoNF/MontoPeriodo/MntExe) cuando el total tributario
   *  viene en 0 (cartolas de corredora, servicios periódicos). Vacío en documentos
   *  normales. Igual que SupplierInvoice.sii_montos de compras. */
  sii_montos?: SiiMontos | null
  /** SII FmaPago: 1=Contado, 2=Crédito, 3=Sin Costo. null si no se pudo extraer del XML. */
  forma_pago: number | null
  /** True si el DTE tiene XML almacenado (xml_dte o xml_envio_dte). */
  has_xml: boolean
  xml_envio_dte: string
  xml_dte: string
  xml_valid: boolean | null
  signature_valid: boolean | null
  ted_valid: boolean | null
  status: string
  status_display: string
  error_message: string
  validation_details: Record<string, any>
  supplier: string | null
  supplier_name: string
  supplier_invoice: string | null
  acceptance_xml: string
  rejection_xml: string
  sii_event_registered: boolean
  /** Eventos SII registrados (ERM/ACD/RCD). Determina las acciones disponibles. */
  sii_events?: SiiEvent[]
  /** Descarte (soft-delete): true si el DTE fue descartado del listado. */
  is_discarded?: boolean
  discarded_at?: string | null
  discarded_by_name?: string | null
  discard_reason?: string
  received_at: string
  created_at: string
  updated_at: string
}

export interface InboundDTEStats {
  total: number
  received: number
  parsed: number
  validated: number
  imported: number
  accepted: number
  rejected: number
  error: number
}

export interface InboundDTEListResponse {
  items: InboundDTE[]
  count: number
  page: number
  page_size: number
  pages: number
}

const keys = {
  all: ['dte-inbound'] as const,
  list: (params?: Record<string, string>) => ['dte-inbound', 'list', params] as const,
  detail: (id: string) => ['dte-inbound', id] as const,
  stats: ['dte-inbound', 'stats'] as const,
}

export function useInboundDTEs(params?: Record<string, string | number>) {
  return useQuery<InboundDTEListResponse>({
    queryKey: keys.list(params as Record<string, string>),
    queryFn: async () => {
      const res = await apiClient.get('/dte-inbound/', { params })
      const d = res.data
      // El backend ahora pagina con {items,count,page,page_size,pages}.
      // Fallback retrocompatible por si llega un array o {results}.
      if (Array.isArray(d)) return { items: d, count: d.length, page: 1, page_size: d.length || 25, pages: 1 }
      if (d.items) return d as InboundDTEListResponse
      const items = d.results ?? []
      return { items, count: d.count ?? items.length, page: 1, page_size: 25, pages: 1 }
    },
  })
}

export function useInboundDTE(id: string) {
  return useQuery<InboundDTE>({
    queryKey: keys.detail(id),
    queryFn: async () => {
      const res = await apiClient.get(`/dte-inbound/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useInboundDTEStats() {
  return useQuery<InboundDTEStats>({
    queryKey: keys.stats,
    queryFn: async () => {
      const res = await apiClient.get('/dte-inbound/stats/')
      return res.data
    },
  })
}

/**
 * Estado de los 3 eventos SII (Ley 19.983) para decidir las acciones disponibles.
 * ERM (acuse de recibo) y ACD (aceptar contenido) NO son excluyentes entre sí;
 * el reclamo (RCD/RFP/RFT) SÍ los excluye.
 */
export function siiEventFlags(dte: Pick<InboundDTE, 'sii_events'>) {
  const evs = dte.sii_events ?? []
  const ok = (tipos: string[]) => evs.some(e => tipos.includes(e.tipo) && e.sii_ok)
  const hasErm = ok(['ERM'])
  const hasAcd = ok(['ACD'])
  const hasReclamo = ok(['RCD', 'RFP', 'RFT'])
  return { hasErm, hasAcd, hasReclamo, positive: hasErm || hasAcd }
}

/** Acuse de recibo de mercaderías (ERM) → registra en SII y despeja el RCV. */
export function useAcuseReciboDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accountId, recinto, observaciones }: {
      id: string; accountId?: string; recinto?: string; observaciones?: string
    }) => {
      // El recinto VIAJA al SII en el `<Recinto>` del EnvioRecibos. Si no se
      // manda, el backend lo resuelve contra el perfil de empresa.
      const body: Record<string, string> = {}
      if (accountId) body.account_id = accountId
      if (recinto) body.recinto = recinto
      if (observaciones) body.observaciones = observaciones
      const res = await apiClient.post(`/dte-inbound/${id}/acuse-recibo/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

/** Aceptar contenido (ACD) → registra en SII + auto-importa a Compras. */
export function useAcceptDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accountId, observaciones }: {
      id: string; accountId?: string; observaciones?: string
    }) => {
      const body: Record<string, string> = {}
      if (accountId) body.account_id = accountId
      if (observaciones) body.observaciones = observaciones
      const res = await apiClient.post(`/dte-inbound/${id}/accept/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useRejectDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason, observaciones }: {
      id: string; reason?: string; observaciones?: string
    }) => {
      // 🔴 El motivo VIAJA al emisor en el `EstadoDTEGlosa`. Sin el, quien
      // recibe el reclamo —que ademas anula su factura— no sabe que corregir.
      const res = await apiClient.post(`/dte-inbound/${id}/reject/`, { reason, observaciones })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useImportDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId?: string }) => {
      const body = accountId ? { account_id: accountId } : {}
      const res = await apiClient.post(`/dte-inbound/${id}/import/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useDiscardDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await apiClient.post(`/dte-inbound/${id}/discard/`, reason ? { reason } : {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useRestoreDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/dte-inbound/${id}/restore/`, {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useUploadDTE() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('archivo', file)
      const res = await apiClient.post('/dte-inbound/upload/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all })
    },
  })
}

// ─── URL helpers para descarga directa ────────────────────────────────────────

export const inboundDteUrls = {
  xml: (id: string) => `${window.location.origin}/api/v1/dte-inbound/${id}/xml/`,
  pdf: (id: string) => `${window.location.origin}/api/v1/dte-inbound/${id}/pdf/`,
}
